import { Button } from '@heidi/design-system/button';
import { Input } from '@heidi/design-system/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heidi/design-system/select';
import { TypographyH2, TypographyP, TypographyP2 } from '@heidi/design-system/typography';
import { type LanguageCode, OrganisationRole } from '@heidi/schema/ml-scribe';
import { type CountryType } from '@heidi/utils/countries';
import {
  getNativeDisplayLanguageName,
  getSupportedLocaleOrDefault,
  isCjkLocale,
} from '@heidi/utils/i18n';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { type Country } from 'react-phone-number-input';

import { getOnboardingProfileHost } from '../../../onboarding-profile-host';
import { type ClinicianTypeInput } from '../../../personalizationTypes';
import { OnboardingStepShell } from '../shared/OnboardingStepShell';
import { useAutoScrollToBottom } from '../shared/useAutoScrollToBottom';
import { aboutYouStepMessages } from './about-you-step.messages';
import { CountryState } from './CountryState';
import { useAboutYouSteps } from './hooks/useAboutYouSteps';
import { PhoneNumberField } from './PhoneNumberField';
import {
  useAllowedPhoneCountries,
  useCountryPhoneMismatch,
} from './PhoneNumberField.utils';
import { roleStepMessages } from './role-step.messages';

interface AboutYouStepProps {
  firstName: string;
  lastName: string;
  specialty: ClinicianTypeInput | null;
  otherSpecialty: string;
  role: OrganisationRole | null;
  country: CountryType | null;
  jurisdiction: string;
  displayLanguage: LanguageCode | null;
  availableDisplayLanguages: LanguageCode[];
  showLanguageDropdown: boolean;
  phoneNumber: string;
  /**
   * The region the sign-in is fixed to, when there is one (an enterprise SSO
   * connection's): only its countries are offered. Absent, every country is.
   */
  countryRegion?: string | null;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onSpecialtyChange: (value: ClinicianTypeInput | null) => void;
  onOtherSpecialtyChange: (value: string) => void;
  onRoleChange: (value: OrganisationRole | null) => void;
  onCountryChange: (value: CountryType | null) => void;
  onJurisdictionChange: (value: string) => void;
  onDisplayLanguageChange: (value: LanguageCode) => void;
  onPhoneNumberChange: (value: string) => void;
  onContinue: () => void;
  isValid: boolean;
}

const FIELD_TRANSITION = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: 'auto' },
  exit: { opacity: 0, height: 0 },
  transition: { duration: 0.2, ease: 'easeOut' },
} as const;
const JURISDICTION_COUNTRY_CODES = new Set(['AU', 'US']);

export function AboutYouStep({
  firstName,
  lastName,
  specialty,
  otherSpecialty,
  role,
  country,
  jurisdiction,
  displayLanguage,
  availableDisplayLanguages,
  showLanguageDropdown,
  phoneNumber,
  countryRegion = null,
  onFirstNameChange,
  onLastNameChange,
  onSpecialtyChange,
  onOtherSpecialtyChange,
  onRoleChange,
  onCountryChange,
  onJurisdictionChange,
  onDisplayLanguageChange,
  onPhoneNumberChange,
  onContinue,
  isValid,
}: AboutYouStepProps) {
  const {
    ClinicianTypeSelect,
    getPhoneNumberVariantForCountry,
    organisationRoleMessages,
    useAboutYouAnalytics,
  } = getOnboardingProfileHost();
  const intl = useIntl();
  const isCjk = isCjkLocale(getSupportedLocaleOrDefault(intl.locale));
  const hasName = firstName.trim() !== '' && lastName.trim() !== '';
  const hasSpecialty = specialty !== null;
  const hasCountry = country !== null;
  const { showSpecialtyField, showRoleField, showCountryField } = useAboutYouSteps({
    hasName,
    hasCountry,
  });
  const showJurisdiction = JURISDICTION_COUNTRY_CODES.has(country?.countryCode ?? '');
  const phoneNumberVariant = getPhoneNumberVariantForCountry(country?.countryCode);
  const showPhoneNumber = phoneNumberVariant !== 'hidden';
  const isPhoneNumberOptional = phoneNumberVariant === 'optional';
  const isPhoneFieldVisible = hasName && hasSpecialty && hasCountry && showPhoneNumber;

  // Filling the last mandatory field reveals the optional ones (phone, display
  // language) plus the newly enabled CTA below it; on a short viewport that all
  // lands under the fold. Scroll the step down to it — no-op when it already
  // fits.
  const scrollRef = useRef<HTMLDivElement>(null);
  useAutoScrollToBottom(
    scrollRef,
    hasName && hasSpecialty && hasCountry && role !== null
  );

  const allowedCountries = useAllowedPhoneCountries();
  const hasCountryPhoneMismatch = useCountryPhoneMismatch(
    country,
    phoneNumber,
    showPhoneNumber
  );

  const analytics = useAboutYouAnalytics({
    country,
    hasCountryPhoneMismatch,
    isPhoneFieldVisible,
    isPhoneNumberOptional,
    onSpecialtyChange,
    onRoleChange,
    onCountryChange,
    onJurisdictionChange,
    onPhoneNumberChange,
  });
  const { handleRoleChange, handlePhoneNumberChange } = analytics;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) onContinue();
  };

  const specialtyField = showSpecialtyField && (
    <m.div key="specialty" {...FIELD_TRANSITION} className="flex flex-col gap-2">
      <TypographyP2>{intl.formatMessage(roleStepMessages.specialty)}</TypographyP2>
      <ClinicianTypeSelect
        selectedSpecialty={specialty ?? undefined}
        setSelectedSpecialty={analytics.handleSpecialtyChange}
        otherSpecialty={otherSpecialty}
        setOtherSpecialty={(value) => onOtherSpecialtyChange(value ?? '')}
        className="[&_button]:bg-surface"
        countryCode={country?.countryCode}
      />
    </m.div>
  );

  const roleField = showRoleField && (
    <m.div key="role" {...FIELD_TRANSITION} className="flex flex-col gap-1.5">
      <TypographyP2>{intl.formatMessage(roleStepMessages.role)}</TypographyP2>
      <Select
        value={role ?? ''}
        onValueChange={(value: OrganisationRole) => handleRoleChange(value)}
      >
        <SelectTrigger id="role" className="bg-surface">
          <SelectValue placeholder={intl.formatMessage(roleStepMessages.selectRole)} />
        </SelectTrigger>
        <SelectContent>
          {Object.values(OrganisationRole).map((roleValue) => (
            <SelectItem key={roleValue} value={roleValue}>
              {intl.formatMessage(organisationRoleMessages[roleValue])}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </m.div>
  );

  const countryField = showCountryField && (
    <m.div key="country" {...FIELD_TRANSITION} className="flex gap-6">
      <CountryState
        country={country}
        jurisdiction={jurisdiction}
        showJurisdiction={showJurisdiction}
        regionFilter={countryRegion ?? undefined}
        onCountrySelected={analytics.handleCountryChange}
        onJurisdictionChange={analytics.handleJurisdictionChange}
      />
    </m.div>
  );

  const firstNameField = (
    <div key="firstName" className="flex min-w-0 flex-1 flex-col gap-1.5">
      <TypographyP2>{intl.formatMessage(aboutYouStepMessages.firstName)}</TypographyP2>
      <Input
        id="firstName"
        data-testid="personalization-first-name"
        value={firstName}
        onChange={(e) => onFirstNameChange(e.target.value)}
        placeholder={intl.formatMessage(aboutYouStepMessages.firstNamePlaceholder)}
        autoComplete="given-name"
        autoFocus={!isCjk}
      />
    </div>
  );

  const lastNameField = (
    <div key="lastName" className="flex min-w-0 flex-1 flex-col gap-1.5">
      <TypographyP2>{intl.formatMessage(aboutYouStepMessages.lastName)}</TypographyP2>
      <Input
        id="lastName"
        value={lastName}
        onChange={(e) => onLastNameChange(e.target.value)}
        placeholder={intl.formatMessage(aboutYouStepMessages.lastNamePlaceholder)}
        autoComplete="family-name"
        autoFocus={isCjk}
      />
    </div>
  );

  const nameFields = isCjk
    ? [lastNameField, firstNameField]
    : [firstNameField, lastNameField];

  const orderedFields = [countryField, roleField, specialtyField];

  return (
    <OnboardingStepShell
      onSubmit={handleSubmit}
      scrollRef={scrollRef}
      contentClassName="gap-10"
      footer={
        <Button size="lg" type="submit" disabled={!isValid} className="w-full">
          {intl.formatMessage(roleStepMessages.continue)}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <TypographyH2>
          {intl.formatMessage(aboutYouStepMessages.letsPersonalise)}
        </TypographyH2>
        <TypographyP className="text-text-primary">
          {intl.formatMessage(aboutYouStepMessages.getStartedInMinutes)}
        </TypographyP>
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex gap-6">{nameFields}</div>

        <AnimatePresence>
          {orderedFields}

          {hasName && hasSpecialty && hasCountry && showLanguageDropdown && (
            <m.div key="language" {...FIELD_TRANSITION} className="flex flex-col gap-1.5">
              <TypographyP2>
                {intl.formatMessage(roleStepMessages.displayLanguage)}
              </TypographyP2>
              <Select
                value={displayLanguage ?? ''}
                onValueChange={(value: string) =>
                  onDisplayLanguageChange(value as LanguageCode)
                }
              >
                <SelectTrigger id="displayLanguage" className="bg-surface">
                  <SelectValue
                    placeholder={intl.formatMessage(
                      roleStepMessages.selectDisplayLanguage
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableDisplayLanguages.map((langCode) => (
                    <SelectItem key={langCode} value={langCode}>
                      {getNativeDisplayLanguageName(langCode)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </m.div>
          )}

          {hasName && hasSpecialty && hasCountry && showPhoneNumber && (
            <m.div key="phone" {...FIELD_TRANSITION} className="flex flex-col gap-1.5">
              <PhoneNumberField
                isOptional={isPhoneNumberOptional}
                phoneNumber={phoneNumber}
                onChange={handlePhoneNumberChange}
                allowedCountries={allowedCountries}
                defaultCountry={country?.countryCode as Country}
                hasCountryPhoneMismatch={hasCountryPhoneMismatch}
              />
            </m.div>
          )}
        </AnimatePresence>
      </div>
    </OnboardingStepShell>
  );
}
